import { QueryClient } from '@tanstack/react-query';
import { EVENT_OVERVIEW_KEY } from './hooks/useEvents';

/**
 * Query-key roots of the data an event overview is computed from: expenses,
 * client payments, guests and their RSVPs, task lists, photos and the
 * event–vendor links. Invalidating any of them means the overview's numbers
 * (header balance, Overview tab, list cards) may have moved.
 */
export const OVERVIEW_SOURCE_KEYS: ReadonlySet<string> = new Set([
  'expenses',
  'clientPayments',
  'guests',
  'invite-recipients',
  'checklists',
  'tasks',
  'photos',
  'entity-links',
  'timelines',
]);

/**
 * Whether invalidating this key may move an overview's numbers: one of the
 * sources above, or the event record itself (`['events']`, `['events', id]` —
 * its budget feeds Budget vs. cost), or its clients (`['events', id, 'users']`).
 * Its other sub-resources (`['events', id, 'tags' | 'contacts' …]`) do not.
 */
export function isOverviewSource(queryKey: readonly unknown[] | undefined): boolean {
  const root = queryKey?.[0];
  if (typeof root !== 'string') return false;
  // The event record, and its clients (the Getting started card counts them).
  if (root === 'events') return queryKey!.length <= 2 || queryKey![2] === 'users';
  return OVERVIEW_SOURCE_KEYS.has(root);
}

type QueryKeyMatch = (queryKey: readonly unknown[]) => boolean;

const byRoot =
  (root: string): QueryKeyMatch =>
  (key) =>
    key[0] === root;
/** An event's email-recipient list (`['events', id, 'vendor-recipients']`). */
const vendorRecipients: QueryKeyMatch = (key) =>
  key[0] === 'events' && key[2] === 'vendor-recipients';
/** A timeline's offered vendor times (`['timelines', 'vendor-times', id]`), read off the bookings. */
const timelineVendorTimes: QueryKeyMatch = (key) =>
  key[0] === 'timelines' && key[1] === 'vendor-times';

/**
 * The events list narrowed by vendor (`['events', { vendorIds }]`): which
 * events a vendor is on is its bookings and booked lines.
 */
const vendorFilteredEvents: QueryKeyMatch = (key) => {
  const filters = key[0] === 'events' ? key[1] : undefined;
  return typeof filters === 'object' && filters !== null && 'vendorIds' in filters;
};

/**
 * Queries that show another query's data, so go stale when it changes. Each
 * entry: a test on the invalidated key, and what else to refresh. Written
 * here once rather than in every mutation that touches the source, which is
 * how some were missed:
 * - adding a vendor to an event left its email checkbox reading "No email";
 * - renaming a vendor left the old name on events and expense rows;
 * - an expense rename or re-vendor left the itemized list showing the old one;
 * - a vendor category added in Settings never reached the vendor pages,
 *   whose list is cached for the session.
 * Refreshed directly, so a dependent does not cascade further, and coalesced:
 * a mutation invalidating several sources (a vendor's detail key and its
 * root) refreshes each dependent once.
 */
const DEPENDENT_QUERIES: readonly { when: QueryKeyMatch; refresh: readonly QueryKeyMatch[] }[] = [
  // A booking is an event–vendor link: its stage, day-of details and grants.
  // Its arrival and load-in times are offered to the event's timelines.
  {
    when: byRoot('entity-links'),
    refresh: [vendorRecipients, byRoot('bookings'), timelineVendorTimes, vendorFilteredEvents],
  },
  {
    when: byRoot('vendors'),
    refresh: [
      byRoot('entity-links'),
      vendorRecipients,
      byRoot('expenses'),
      byRoot('planning-lists'),
      byRoot('needs'),
      byRoot('quotes'),
      byRoot('bookings'),
      timelineVendorTimes,
      vendorFilteredEvents,
      // A decor piece names the vendors who supply it.
      byRoot('decor-library'),
    ],
  },
  // A booking's needs and money are its vendor's booked lines.
  {
    when: byRoot('expenses'),
    refresh: [byRoot('planning-lists'), byRoot('needs'), byRoot('bookings'), vendorFilteredEvents],
  },
  // A booking's files count (the contract, the insurance certificate).
  // A decor piece's pictures are its attachments.
  { when: byRoot('entity-attachments'), refresh: [byRoot('bookings'), byRoot('decor-library')] },
  // A need's items and quotes feed its row on the Plan view.
  { when: byRoot('planning-lists'), refresh: [byRoot('needs')] },
  // An item's open-task count on its row in the Items grid.
  { when: byRoot('item-tasks'), refresh: [byRoot('planning-lists')] },
  { when: byRoot('quotes'), refresh: [byRoot('needs')] },
  {
    when: (key) => key[0] === 'settings' && key[1] === 'vendorCategories',
    refresh: [byRoot('vendor-categories'), byRoot('bookings')],
  },
  // A booking answers the office's stage list with it (labels, kinds, order).
  {
    when: (key) => key[0] === 'settings' && key[1] === 'appSettingLists',
    refresh: [byRoot('bookings')],
  },
];

/** What else to refresh when `queryKey` is invalidated (empty for most keys). */
export function dependentQueries(queryKey: readonly unknown[] | undefined): QueryKeyMatch[] {
  if (!queryKey) return [];
  return DEPENDENT_QUERIES.filter((d) => d.when(queryKey)).flatMap((d) => [...d.refresh]);
}

/**
 * The app's query client. Invalidating one of the overview's sources also
 * invalidates the overviews, so the numbers never go stale on an open page —
 * and only then: an unrelated change (an event tag, a setting) costs nothing.
 * Following the invalidation rather than the mutation also catches a source
 * whose own query is not in the cache (ticking a task on the Overview tab,
 * with the task lists never opened). Several sources invalidated by one
 * mutation refresh the overviews once. It also refreshes the queries that
 * show a source's data (`DEPENDENT_QUERIES`).
 */
export class AppQueryClient extends QueryClient {
  private overviewRefreshQueued = false;
  private pendingDependents = new Set<QueryKeyMatch>();

  override invalidateQueries(
    ...args: Parameters<QueryClient['invalidateQueries']>
  ): ReturnType<QueryClient['invalidateQueries']> {
    if (isOverviewSource(args[0]?.queryKey) && !this.overviewRefreshQueued) {
      this.overviewRefreshQueued = true;
      queueMicrotask(() => {
        this.overviewRefreshQueued = false;
        void super.invalidateQueries({ queryKey: [EVENT_OVERVIEW_KEY] });
      });
    }
    const dependents = dependentQueries(args[0]?.queryKey);
    if (dependents.length > 0) {
      const flushQueued = this.pendingDependents.size > 0;
      for (const match of dependents) this.pendingDependents.add(match);
      if (!flushQueued) {
        queueMicrotask(() => {
          const matches = [...this.pendingDependents];
          this.pendingDependents.clear();
          void super.invalidateQueries({
            predicate: (query) => matches.some((match) => match(query.queryKey)),
          });
        });
      }
    }
    return super.invalidateQueries(...args);
  }
}

export function createAppQueryClient(): AppQueryClient {
  return new AppQueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000,
        retry: 1,
      },
    },
  });
}
